import React, { useState, useEffect } from 'react';
import { Send, CheckCircle, AlertCircle, Loader, Phone, Mail, MapPin, MessageCircle, Calendar, Globe, Building } from 'lucide-react';
import { contactAPI, contactFormOptionsAPI, contactFormFieldsAPI } from '../../services/api';
import { useFormSubmit } from '../../hooks/useApi';
import GoogleReCAPTCHA from '../Google/reCAPTCHA';
import { useGoogleAnalytics } from '../../hooks/useGoogleAnalytics';

const getIconComponent = (iconName) => {
  const icons = {
    'Mail': Mail,
    'Phone': Phone,
    'MapPin': MapPin,
    'MessageCircle': MessageCircle,
    'Calendar': Calendar,
    'Globe': Globe,
    'Building': Building
  };
  return icons[iconName] || Phone;
};

const ContactForm = ({ variant = 'full', otherContactOptions = [] }) => {
  const { trackFormSubmission } = useGoogleAnalytics();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    service: '',
    budget: '',
    timeline: ''
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [formOptions, setFormOptions] = useState({
    services: [],
    budgets: [],
    timelines: []
  });

  const [formFields, setFormFields] = useState({});
  const { submit, loading, error, success } = useFormSubmit(contactAPI.submit);

  // Fetch form options and fields from API
  useEffect(() => {
    const fetchFormData = async () => {
      try {
        const [optionsResponse, fieldsResponse] = await Promise.all([
          contactFormOptionsAPI.getOptions(),
          contactFormFieldsAPI.getFields()
        ]);

        if (optionsResponse?.data?.success) {
          setFormOptions(optionsResponse.data.data);
        }

        if (fieldsResponse?.data?.success) {
          const fieldsMap = {};
          fieldsResponse.data.data.forEach(field => {
            fieldsMap[field.field_name] = field;
          });
          setFormFields(fieldsMap);
        }
      } catch (error) {
        console.error('Error fetching form data:', error);
        // Fallback to default options if API fails
        setFormOptions({
          services: [
            { value: '', label: 'Select a service' },
            { value: 'web_development', label: 'Web Development' },
            { value: 'mobile_app', label: 'Mobile App Development' },
            { value: 'digital_marketing', label: 'Digital Marketing' },
            { value: 'seo', label: 'SEO Services' },
            { value: 'ui_ux', label: 'UI/UX Design' },
            { value: 'ecommerce', label: 'E-commerce Solutions' },
            { value: 'consulting', label: 'IT Consulting' },
            { value: 'other', label: 'Other' }
          ],
          budgets: [
            { value: '', label: 'Select budget range' },
            { value: '10000-25000', label: '₹10,000 - ₹25,000' },
            { value: '25000-50000', label: '₹25,000 - ₹50,000' },
            { value: '50000-100000', label: '₹50,000 - ₹1,00,000' },
            { value: '100000-250000', label: '₹1,00,000 - ₹2,50,000' },
            { value: '250000+', label: '₹2,50,000+' }
          ],
          timelines: [
            { value: '', label: 'Select timeline' },
            { value: 'asap', label: 'ASAP' },
            { value: '1-2_weeks', label: '1-2 weeks' },
            { value: '1_month', label: '1 month' },
            { value: '2-3_months', label: '2-3 months' },
            { value: '3-6_months', label: '3-6 months' },
            { value: '6+_months', label: '6+ months' }
          ]
        });

        // Set default fields if API fails
        setFormFields({
          name: { field_label: 'Full Name*', field_placeholder: 'Your full name' },
          email: { field_label: 'Email Address*', field_placeholder: 'your.email@example.com' },
          phone: { field_label: 'Phone Number*', field_placeholder: '+91 9876543210' },
          company: { field_label: 'Company Name', field_placeholder: 'Your company name' },
          subject: { field_label: 'Subject*', field_placeholder: 'What\'s your project about?' },
          service: { field_label: 'Service Needed*', field_placeholder: 'Select a service' },
          budget: { field_label: 'Budget Range', field_placeholder: 'Select budget range' },
          timeline: { field_label: 'Timeline', field_placeholder: 'Select timeline' },
          message: { field_label: 'Message*', field_placeholder: 'Tell us about your project...' },
          submit_button: { field_label: 'Send Message', field_placeholder: 'Send Message' }
        });
      }
    };

    fetchFormData();
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    }

    if (variant === 'full') {
      if (!formData.subject.trim()) {
        newErrors.subject = 'Subject is required';
      }
      if (!formData.service) {
        newErrors.service = 'Please select a service';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Check reCAPTCHA (skip if disabled)
    if (recaptchaToken !== 'DISABLED' && !recaptchaToken) {
      setErrors({ ...errors, recaptcha: 'Please complete the reCAPTCHA verification' });
      return;
    }

    try {
      trackFormSubmission('contact_form', true);
      const formDataWithRecaptcha = {
        ...formData,
        recaptchaToken
      };
      
      await submit(formDataWithRecaptcha);
      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: '',
        service: '',
        budget: '',
        timeline: ''
      });
      setRecaptchaToken(null);
    } catch (error) {
      console.error('Error submitting form:', error);
      trackFormSubmission('contact_form', false);
    }
  };

  if (submitted && success) {
    return (
      <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 text-center border border-white/20">
        <div className="flex items-center justify-center mb-6">
          <CheckCircle className="w-16 h-16 text-green-500 dark:text-green-400" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          Message Sent Successfully!
        </h3>
        <p className="text-gray-600 dark:text-gray-300 mb-8 text-lg">
          Thank you for contacting us. We'll get back to you soon.
        </p>
        <button
          onClick={() => setSubmitted(false)}
          className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-glow"
        >
          Send Another Message
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
              {formFields.name?.field_label || 'Full Name *'}
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-4 py-3 border rounded-2xl shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white transition-all duration-300 ${
                errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
              placeholder={formFields.name?.field_placeholder || 'Your full name'}
            />
            {errors.name && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.name}</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
              {formFields.email?.field_label || 'Email Address *'}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-4 py-3 border rounded-2xl shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white transition-all duration-300 ${
                errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
              placeholder={formFields.email?.field_placeholder || 'your.email@example.com'}
            />
            {errors.email && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.email}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
              {formFields.phone?.field_label || 'Phone Number *'}
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={`w-full px-4 py-3 border rounded-2xl shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white transition-all duration-300 ${
                errors.phone ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
              placeholder={formFields.phone?.field_placeholder || '+91 9876543210'}
            />
            {errors.phone && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.phone}</p>
            )}
          </div>

          <div>
            <label htmlFor="company" className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
              {formFields.company?.field_label || 'Company Name'}
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-2xl shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white transition-all duration-300"
              placeholder={formFields.company?.field_placeholder || 'Your company name'}
            />
          </div>
        </div>

        {variant === 'full' && (
          <>
            <div>
              <label htmlFor="subject" className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
                {formFields.subject?.field_label || 'Subject *'}
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-2xl shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white transition-all duration-300 ${
                  errors.subject ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={formFields.subject?.field_placeholder || 'What\'s your project about?'}
              />
              {errors.subject && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.subject}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <label htmlFor="service" className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  {formFields.service?.field_label || 'Service Needed *'}
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-2xl shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all duration-300 ${
                    errors.service ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <option value="" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">{formFields.service?.field_placeholder || 'Select a service'}</option>
                  {formOptions.services.map(option => (
                    <option key={option.value} value={option.value} className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.service && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.service}</p>
                )}
              </div>

              <div>
                <label htmlFor="budget" className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  {formFields.budget?.field_label || 'Budget Range'}
                </label>
                <select
                  id="budget"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-2xl shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all duration-300"
                >
                  <option value="" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">{formFields.budget?.field_placeholder || 'Select budget range'}</option>
                  {formOptions.budgets.map(option => (
                    <option key={option.value} value={option.value} className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="timeline" className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  {formFields.timeline?.field_label || 'Timeline'}
                </label>
                <select
                  id="timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-2xl shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all duration-300"
                >
                  <option value="" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">{formFields.timeline?.field_placeholder || 'Select timeline'}</option>
                  {formOptions.timelines.map(option => (
                    <option key={option.value} value={option.value} className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}

        {/* Message */}
        <div>
          <label htmlFor="message" className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
            {formFields.message?.field_label || 'Message *'}
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows="6"
            className={`w-full px-4 py-3 border rounded-2xl shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white resize-none transition-all duration-300 ${
              errors.message ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
            }`}
            placeholder={formFields.message?.field_placeholder || 'Tell us about your project, requirements, and any specific details you\'d like to share...'}
          />
          {errors.message && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.message}</p>
          )}
        </div>

        {/* reCAPTCHA */}
        <div>
          <GoogleReCAPTCHA
            page="contact"
            onVerify={setRecaptchaToken}
            onError={(errors) => {
              setErrors({ ...errors, recaptcha: 'reCAPTCHA verification failed' });
            }}
            className="mt-4"
          />
          {errors.recaptcha && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.recaptcha}</p>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            * Required fields
          </div>
          <button
            type="submit"
            disabled={loading || !recaptchaToken}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-2xl font-bold hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-all duration-300 flex items-center gap-3 disabled:opacity-50 shadow-lg hover:shadow-xl"
          >
            {loading ? (
              <>
                <Loader className="w-6 h-6 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-6 h-6" />
                {formFields.submit_button?.field_label || 'Send Message'}
              </>
            )}
          </button>
        </div>

        {error && (
          <div className="p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl">
            <div className="flex items-center">
              <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400 mr-3" />
              <p className="text-red-800 dark:text-red-200 font-medium">
                {error}
              </p>
            </div>
          </div>
        )}
      </form>

      {/* Other Ways to Reach Us */}
      {otherContactOptions && otherContactOptions.length > 0 && (
        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
          <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
            Other Ways to Reach Us
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {otherContactOptions.map((option, index) => {
              const IconComponent = getIconComponent(option.icon);
              return (
                <div
                  key={index}
                  className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl p-6 hover:scale-105 transition-all duration-300 border border-white/20 shadow-lg"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0 mr-4">
                      <IconComponent className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 dark:text-white mb-1">
                        {option.title}
                      </h5>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                        {option.description}
                      </p>
                      <p className="text-sm font-bold text-blue-600 dark:text-blue-400">
                        {option.value}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactForm;
